import React, { useEffect, useRef, useState, useCallback } from "react";
import {
  Send, Plus, Trash2, ShieldAlert, Sparkles, PlugZap, CheckCircle2,
  XCircle, Clock, AlertTriangle, Lock, MessageSquare, Loader2, Gauge, Info,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { marketingApi } from "../marketingApi";

/* ---------- helpers ---------- */
const fmtTime = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString("es-ES", {
      day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit",
    });
  } catch { return "—"; }
};
const fmtTokens = (n) => {
  if (n === null || n === undefined) return "—";
  const v = Number(n);
  if (!isFinite(v)) return "—";
  return v.toLocaleString("es-ES");
};
const genIdempotency = () =>
  `appr_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;

/* ---------- token alerts banner (visible to all marketing users) ---------- */
function TokenAlertsBanner({ alerts }) {
  if (!alerts || alerts.length === 0) return null;
  // Sort by threshold descending so the highest active alert is on top.
  const sorted = [...alerts].sort((a, b) => (b.threshold || 0) - (a.threshold || 0));
  const top = sorted[0];
  const pct = Math.round((top.threshold || 0) * 100);
  const tone = pct >= 100
    ? { bg: "bg-red-50", border: "border-red-200", text: "text-red-800", icon: "text-red-600" }
    : pct >= 90
    ? { bg: "bg-amber-50", border: "border-amber-200", text: "text-amber-900", icon: "text-amber-700" }
    : { bg: "bg-amber-50/60", border: "border-amber-200", text: "text-amber-900", icon: "text-amber-600" };
  return (
    <div
      role="alert"
      data-testid="director-token-alerts"
      className={`border-b px-4 py-2 text-xs flex items-start gap-2 ${tone.bg} ${tone.border} ${tone.text}`}
    >
      <AlertTriangle size={14} className={`${tone.icon} shrink-0 mt-0.5`} />
      <div className="flex-1">
        <b>Presupuesto IA al {pct}%</b>
        <span className="text-slate-700">
          {" "}· {Number(top.used_tokens || 0).toLocaleString("es-ES")} /
          {" "}{Number(top.budget_tokens || 0).toLocaleString("es-ES")} tokens ({top.month}, Europe/Madrid).
          {" "}Pide a un administrador que revise el límite en Auditoría.
        </span>
      </div>
    </div>
  );
}

/* ---------- token meter (compact header widget) ---------- */
function TokenMeter({ usage, compact = false }) {
  const [open, setOpen] = useState(false);
  // Resilient rendering: if the backend has not yet responded we still render
  // the meter chip (with zeroes + "sin definir") so operators always see the
  // widget — including on the empty state, on a brand-new conversation, and
  // on mobile viewports. This matches the product rule: the counter MUST be
  // visible even with 0 tokens and no budget.
  const safe = usage || {
    this_chat: {}, this_month: {}, budget: { defined: false },
  };
  const budget = safe.budget || {};
  const thisChat = safe.this_chat || {};
  const thisMonth = safe.this_month || {};
  const remainingLabel = budget.defined
    ? fmtTokens(budget.remaining_tokens)
    : "sin definir";
  const pct = budget.defined && budget.pct_used != null
    ? Math.min(100, Math.round(budget.pct_used * 100))
    : null;
  const barColor = pct == null ? "bg-slate-200"
    : pct >= 100 ? "bg-red-500"
    : pct >= 90 ? "bg-amber-500"
    : pct >= 70 ? "bg-amber-400"
    : "bg-emerald-500";
  const unreported = (thisChat.turns_no_usage || 0) + (thisMonth.turns_no_usage || 0);
  const loading = !usage;
  return (
    <div className="relative" data-testid="token-meter-root">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label="Uso de tokens del Director IA"
        data-testid="token-meter"
        className={`inline-flex items-center gap-2 rounded-full border border-[#CCE2F8] bg-white px-2.5 py-1 text-[11px] text-[#0A2A5E] hover:bg-[#F3F8FE] transition-colors ${loading ? "opacity-70" : ""}`}
      >
        <Gauge size={11} />
        <span data-testid="token-meter-this-chat">
          Chat <b className="tabular-nums">{fmtTokens(thisChat.total_tokens || 0)}</b>
        </span>
        {!compact && <span className="text-slate-300">·</span>}
        <span data-testid="token-meter-this-month" className={compact ? "hidden sm:inline" : ""}>
          Mes <b className="tabular-nums">{fmtTokens(thisMonth.total_tokens || 0)}</b>
        </span>
        {!compact && <span className="text-slate-300 hidden sm:inline">·</span>}
        <span data-testid="token-meter-remaining" className={compact ? "hidden md:inline" : ""}>
          Restante <b className="tabular-nums">{remainingLabel}</b>
        </span>
        <Info size={11} className="text-slate-400" />
      </button>
      {open && (
        <div
          role="dialog"
          data-testid="token-meter-tooltip"
          className="absolute right-0 top-full mt-2 z-50 w-80 rounded-xl border border-[#CCE2F8] bg-white shadow-lg p-3 text-[11px] text-slate-700"
        >
          <div className="font-semibold text-[#0A2A5E] mb-1.5 flex items-center gap-1.5">
            <Gauge size={12} /> Director IA · uso real por API ({usage.month})
          </div>
          <div className="grid grid-cols-2 gap-x-3 gap-y-1 tabular-nums">
            <span className="text-slate-500">Chat · prompt</span>
            <span className="text-right">{fmtTokens(thisChat.input_tokens)}</span>
            <span className="text-slate-500">Chat · respuesta</span>
            <span className="text-right">{fmtTokens(thisChat.output_tokens)}</span>
            {thisChat.cached_tokens > 0 && (<>
              <span className="text-slate-500">Chat · caché</span>
              <span className="text-right">{fmtTokens(thisChat.cached_tokens)}</span>
            </>)}
            {thisChat.reasoning_tokens > 0 && (<>
              <span className="text-slate-500">Chat · razonamiento</span>
              <span className="text-right">{fmtTokens(thisChat.reasoning_tokens)}</span>
            </>)}
            <span className="text-slate-500">Chat · total</span>
            <span className="text-right font-semibold">{fmtTokens(thisChat.total_tokens)}</span>
            <span className="col-span-2 border-t border-slate-100 my-1" />
            <span className="text-slate-500">Mes · prompt</span>
            <span className="text-right">{fmtTokens(thisMonth.input_tokens)}</span>
            <span className="text-slate-500">Mes · respuesta</span>
            <span className="text-right">{fmtTokens(thisMonth.output_tokens)}</span>
            <span className="text-slate-500">Mes · total</span>
            <span className="text-right font-semibold">{fmtTokens(thisMonth.total_tokens)}</span>
          </div>
          {budget.defined ? (
            <>
              <div className="mt-2 flex items-center justify-between">
                <span className="text-slate-500">Límite mensual interno</span>
                <span className="tabular-nums font-semibold text-[#0A2A5E]">
                  {fmtTokens(budget.monthly_tokens)}
                </span>
              </div>
              <div className="mt-1 h-1.5 w-full rounded bg-slate-100 overflow-hidden">
                <div
                  data-testid="token-meter-bar"
                  className={`h-full ${barColor} transition-all`}
                  style={{ width: `${pct == null ? 0 : pct}%` }}
                />
              </div>
              <div className="mt-1 flex items-center justify-between">
                <span className="text-slate-500">Restante</span>
                <span className="tabular-nums text-slate-700">
                  {fmtTokens(budget.remaining_tokens)} · {pct == null ? "—" : `${pct}%`}
                </span>
              </div>
              <div className="mt-1 text-[10px] text-slate-400">
                {budget.enforcement_enabled
                  ? "Corte automático al 100% activo (configurable por admin)."
                  : "Sólo avisos activos (sin corte automático)."}
              </div>
            </>
          ) : (
            <div className="mt-2 rounded border border-dashed border-slate-200 bg-slate-50 px-2 py-1.5 text-[10px] text-slate-600" data-testid="token-meter-no-budget">
              <b>Presupuesto no definido.</b> Un administrador puede fijar el
              límite mensual desde Auditoría &gt; Director IA · presupuesto.
            </div>
          )}
          {unreported > 0 && (
            <div className="mt-2 text-[10px] text-amber-700">
              {unreported} turno(s) sin usage informado por el proveedor.
            </div>
          )}
          <div className="mt-2 pt-2 border-t border-slate-100 text-[10px] text-slate-400">
            Créditos reales del proveedor: <b>No disponible mediante API</b>.
            Revísalos en el panel de Emergent.
          </div>
        </div>
      )}
    </div>
  );
}

/* ---------- status badges ---------- */
function StatusBadge({ status, writesEnabled, killEnabled }) {
  if (killEnabled)
    return (
      <span data-testid="director-status-killed" className="inline-flex items-center gap-1 rounded-full bg-red-50 text-red-700 border border-red-200 px-2.5 py-1 text-xs font-semibold">
        <ShieldAlert size={12} /> Kill switch activo
      </span>
    );
  if (status?.model_available === false)
    return (
      <span data-testid="director-status-nomodel" className="inline-flex items-center gap-1 rounded-full bg-amber-50 text-amber-700 border border-amber-200 px-2.5 py-1 text-xs font-semibold">
        <AlertTriangle size={12} /> IA no configurada
      </span>
    );
  return (
    <span data-testid="director-status-ok" className="inline-flex items-center gap-1 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 px-2.5 py-1 text-xs font-semibold">
      <CheckCircle2 size={12} /> IA conectada {writesEnabled ? "· escritura ON" : "· solo lectura"}
    </span>
  );
}

/* ---------- evidence chip ---------- */
function EvidenceChip({ e }) {
  const period = e?.period ? (
    typeof e.period === "object"
      ? Object.values(e.period).filter(Boolean).slice(0, 2).join(" → ")
      : String(e.period)
  ) : "—";
  return (
    <div className="rounded-lg border border-[#CCE2F8] bg-[#F3F8FE] px-3 py-2 text-xs">
      <div className="flex items-center gap-2">
        <span className="font-semibold text-[#0A2A5E]">{e?.source || "—"}</span>
        <span className="text-slate-400">·</span>
        <span className="text-slate-500">{period}</span>
      </div>
      {e?.note && <div className="mt-0.5 text-slate-600">{e.note}</div>}
      {e?.metrics && (
        <div className="mt-1 flex flex-wrap gap-1">
          {Object.entries(e.metrics).slice(0, 4).map(([k, v]) => (
            <span key={k} className="rounded bg-white border border-[#CCE2F8] px-1.5 py-0.5 text-[10px] text-slate-600 tabular-nums">
              {k}: <b className="text-slate-800">{typeof v === "number" ? v.toLocaleString("es-ES") : String(v).slice(0, 40)}</b>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

/* ---------- proposal card ---------- */
function ProposalCard({ proposalId, onDone }) {
  const [p, setP] = useState(null);
  const [approving, setApproving] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [open, setOpen] = useState(false);

  const load = useCallback(async () => {
    try { setP(await marketingApi.director.proposalGet(proposalId)); }
    catch (e) { toast.error("No se pudo cargar la propuesta"); }
  }, [proposalId]);
  useEffect(() => { load(); }, [load]);

  if (!p) return (
    <div data-testid={`proposal-skeleton-${proposalId}`} className="rounded-xl border border-slate-200 bg-white p-4 animate-pulse h-24" />
  );

  const isPending = p.status === "pending";
  const platformLabel = {
    meta_ads: "Meta Ads", instagram: "Instagram", facebook: "Facebook",
    wordpress: "WordPress", brevo: "Brevo", editorial: "Editorial",
  }[p.platform] || p.platform;

  const approve = async () => {
    if (!p.nonce) { toast.error("Nonce caducado, recarga la propuesta"); await load(); return; }
    setApproving(true);
    try {
      const res = await marketingApi.director.proposalApprove(p.id, {
        nonce: p.nonce, idempotency_key: genIdempotency(), confirm_text: "EJECUTAR",
      });
      if (res.ok) {
        toast.success(res.status === "prepared_not_executed"
          ? "Aprobada y auditada. Escritura externa deshabilitada (preparado, no ejecutado)."
          : "Aprobada y ejecutada.");
        onDone?.();
        await load();
      } else {
        toast.error(`No se aprobó: ${res.reason || res.status}`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al aprobar");
    } finally {
      setApproving(false);
    }
  };

  const reject = async () => {
    setRejecting(true);
    try {
      await marketingApi.director.proposalReject(p.id, { reason: "Rechazada por el usuario" });
      toast.success("Propuesta rechazada");
      onDone?.();
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al rechazar");
    } finally {
      setRejecting(false);
    }
  };

  const statusBadge = {
    pending:               { label: "Pendiente de aprobación", color: "amber", icon: Clock },
    approved:              { label: "Aprobada", color: "emerald", icon: CheckCircle2 },
    prepared_not_executed: { label: "Preparada · NO ejecutada (writes OFF)", color: "sky", icon: Lock },
    executed:              { label: "Ejecutada", color: "emerald", icon: CheckCircle2 },
    rejected:              { label: "Rechazada", color: "slate", icon: XCircle },
    killed:                { label: "Bloqueada por kill switch", color: "red", icon: ShieldAlert },
    expired:               { label: "Expirada", color: "slate", icon: XCircle },
  }[p.status] || { label: p.status, color: "slate", icon: Clock };
  const Icon = statusBadge.icon;

  return (
    <div
      data-testid={`proposal-card-${p.id}`}
      role="region"
      aria-label={`Propuesta ${platformLabel} ${p.action}`}
      className="rounded-xl border border-[#CCE2F8] bg-white p-4"
    >
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="inline-flex items-center gap-1 rounded-md bg-[#0A2A5E] text-white px-2 py-0.5 text-[11px] font-semibold">
              {platformLabel}
            </span>
            <span className="text-xs font-mono text-slate-500">{p.action}</span>
          </div>
          <h4 className="mt-1 text-sm font-semibold text-slate-900">{p.summary || "Acción propuesta"}</h4>
          {p.target?.asset_name && (
            <div className="text-xs text-slate-500 mt-0.5">
              Objetivo: <b className="text-slate-700">{p.target.asset_name}</b>
              {p.target.account_id && <span className="text-slate-400"> · {p.target.account_id}</span>}
            </div>
          )}
        </div>
        <span className={`inline-flex items-center gap-1 rounded-full bg-${statusBadge.color}-50 text-${statusBadge.color}-700 border border-${statusBadge.color}-200 px-2 py-0.5 text-[11px] font-semibold`}>
          <Icon size={11} />
          {statusBadge.label}
        </span>
      </div>

      {p.risks?.length > 0 && (
        <ul className="mt-3 space-y-1 text-xs text-amber-700">
          {p.risks.slice(0, 3).map((r, i) => (
            <li key={i} className="flex gap-1.5">
              <AlertTriangle size={12} className="mt-0.5 shrink-0" /> {r}
            </li>
          ))}
        </ul>
      )}

      <button
        type="button"
        className="mt-3 text-[11px] text-[#0A2A5E] font-semibold hover:underline"
        onClick={() => setOpen((v) => !v)}
        data-testid={`proposal-toggle-${p.id}`}
      >
        {open ? "Ocultar detalle" : "Ver diff antes/después"}
      </button>
      {open && (
        <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2 text-[11px]">
          <pre className="rounded border border-slate-200 bg-slate-50 p-2 overflow-x-auto">
Before:{"\n"}{JSON.stringify(p.before || {}, null, 2)}
          </pre>
          <pre className="rounded border border-slate-200 bg-slate-50 p-2 overflow-x-auto">
After:{"\n"}{JSON.stringify(p.after || {}, null, 2)}
          </pre>
          {p.copy_text && (
            <div className="sm:col-span-2 rounded border border-slate-200 bg-slate-50 p-2">
              <div className="text-slate-500 mb-1">Copy propuesto:</div>
              <div className="text-slate-800 whitespace-pre-wrap">{p.copy_text}</div>
            </div>
          )}
          {p.budget_delta_usd !== null && p.budget_delta_usd !== undefined && (
            <div className="sm:col-span-2 text-slate-700">
              Cambio de presupuesto propuesto: <b>{p.budget_delta_usd > 0 ? "+" : ""}{p.budget_delta_usd} USD</b>
            </div>
          )}
          {p.schedule_at && (
            <div className="sm:col-span-2 text-slate-700">
              Programación: <b>{p.schedule_at}</b> ({p.timezone})
            </div>
          )}
        </div>
      )}

      {isPending && (
        <div className="mt-3 flex flex-wrap gap-2">
          <Button
            size="sm"
            onClick={approve}
            disabled={approving || rejecting}
            data-testid={`proposal-approve-${p.id}`}
            className="bg-[#0A2A5E] hover:bg-[#0A2A5E]/90"
          >
            {approving ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <CheckCircle2 size={14} className="mr-1.5" />}
            Aprobar y ejecutar
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={reject}
            disabled={approving || rejecting}
            data-testid={`proposal-reject-${p.id}`}
          >
            <XCircle size={14} className="mr-1.5" /> Rechazar
          </Button>
          <span className="text-[10px] text-slate-400 self-center">
            La aprobación requiere confirmación explícita y expira en 15 min.
          </span>
        </div>
      )}

      {p.execution_result && (
        <div className="mt-3 text-[11px] rounded bg-slate-50 border border-slate-200 p-2">
          <div className="font-semibold text-slate-700">Resultado</div>
          <div className="text-slate-600 mt-0.5">{p.execution_result.note || p.execution_result.status}</div>
        </div>
      )}
    </div>
  );
}

/* ---------- single message bubble ---------- */
function Bubble({ msg, onProposalsChange }) {
  const isUser = msg.role === "user";
  return (
    <div
      data-testid={`msg-${msg.role}-${msg.id}`}
      className={`flex ${isUser ? "justify-end" : "justify-start"} w-full`}
    >
      <div className={`max-w-[90%] sm:max-w-[80%] ${isUser ? "" : "w-full"}`}>
        <div
          className={isUser
            ? "rounded-2xl rounded-tr-sm bg-[#0A2A5E] text-white px-4 py-2.5 shadow-sm"
            : "rounded-2xl rounded-tl-sm bg-white border border-[#CCE2F8] px-4 py-3 shadow-sm"}
        >
          <div className={`whitespace-pre-wrap text-sm ${isUser ? "" : "text-slate-800"}`}>
            {msg.text}
          </div>
        </div>
        <div className={`mt-1 text-[10px] text-slate-400 ${isUser ? "text-right" : ""}`}>
          {fmtTime(msg.created_at)}
          {!isUser && msg.model && <span> · {msg.model}</span>}
          {!isUser && msg.status && msg.status !== "ok" && (
            <span className="ml-1 text-amber-600">· {msg.status}</span>
          )}
        </div>
        {!isUser && msg.evidence?.length > 0 && (
          <div className="mt-2 space-y-1.5" data-testid={`msg-evidence-${msg.id}`}>
            {msg.evidence.map((e, i) => <EvidenceChip key={i} e={e} />)}
          </div>
        )}
        {!isUser && msg.proposal_ids?.length > 0 && (
          <div className="mt-3 space-y-2" data-testid={`msg-proposals-${msg.id}`}>
            {msg.proposal_ids.map((pid) => (
              <ProposalCard key={pid} proposalId={pid} onDone={onProposalsChange} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/* ---------- main director tab ---------- */
export default function DirectorTab() {
  const [convs, setConvs] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [status, setStatus] = useState({ model_available: null, writes_enabled: false, kill_switch: { enabled: false } });
  const [tokenUsage, setTokenUsage] = useState(null);
  const [tokenAlerts, setTokenAlerts] = useState([]);
  const [showMobileList, setShowMobileList] = useState(false);
  const scrollRef = useRef(null);

  const refreshStatus = useCallback(async () => {
    try { setStatus(await marketingApi.director.status()); } catch {}
  }, []);

  const refreshTokenUsage = useCallback(async (convId) => {
    try {
      const [u, a] = await Promise.all([
        marketingApi.director.tokenUsage(convId || undefined),
        marketingApi.director.tokenAlerts().catch(() => ({ alerts: [] })),
      ]);
      setTokenUsage(u);
      setTokenAlerts(a?.alerts || []);
    } catch { /* non-blocking */ }
  }, []);

  const refreshConvs = useCallback(async () => {
    try {
      const d = await marketingApi.director.convList();
      setConvs(d.conversations || []);
    } catch (e) { toast.error("No se pudo cargar la lista de conversaciones"); }
  }, []);

  const openConv = useCallback(async (id) => {
    setActiveId(id);
    setShowMobileList(false);
    try {
      const d = await marketingApi.director.convGet(id);
      setMessages(d.messages || []);
      refreshTokenUsage(id);
    } catch { toast.error("No se pudo abrir la conversación"); }
  }, [refreshTokenUsage]);

  const newConv = async () => {
    try {
      const c = await marketingApi.director.convCreate({});
      await refreshConvs();
      await openConv(c.id);
    } catch { toast.error("No se pudo crear la conversación"); }
  };

  const deleteConv = async (id, e) => {
    e?.stopPropagation();
    if (!window.confirm("¿Eliminar esta conversación?")) return;
    try {
      await marketingApi.director.convDelete(id);
      if (activeId === id) { setActiveId(null); setMessages([]); }
      await refreshConvs();
    } catch { toast.error("Error al borrar"); }
  };

  const send = async () => {
    const text = input.trim();
    if (!text) return;
    if (!activeId) { await newConv(); return; }
    setSending(true);
    const temp = {
      id: `temp-${Date.now()}`, role: "user", text, created_at: new Date().toISOString(),
    };
    setMessages((m) => [...m, temp]);
    setInput("");
    try {
      const r = await marketingApi.director.sendMessage(activeId, { text });
      setMessages((m) => {
        const base = m.filter((x) => x.id !== temp.id);
        return [...base, r.user_message, r.assistant_message];
      });
      // refresh conversation list for title updates + token meter
      refreshConvs();
      refreshTokenUsage(activeId);
    } catch (e) {
      setMessages((m) => m.filter((x) => x.id !== temp.id));
      toast.error(e?.response?.data?.detail || "Error enviando mensaje");
    } finally {
      setSending(false);
    }
  };

  useEffect(() => { refreshStatus(); refreshConvs(); refreshTokenUsage(null); }, [refreshStatus, refreshConvs, refreshTokenUsage]);
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages]);

  const killEnabled = !!status?.kill_switch?.enabled;

  return (
    <div className="flex flex-col lg:flex-row gap-4 h-[calc(100vh-280px)] min-h-[500px]" data-testid="director-tab">
      {/* Mobile toggle */}
      <div className="lg:hidden flex items-center justify-between gap-2 flex-wrap">
        <button
          onClick={() => setShowMobileList((v) => !v)}
          className="inline-flex items-center gap-1.5 rounded-lg border border-[#CCE2F8] bg-white px-3 py-1.5 text-xs font-medium text-[#0A2A5E]"
          data-testid="director-mobile-toggle-list"
        >
          <MessageSquare size={14} /> {convs.length} chats
        </button>
        <div className="flex items-center gap-2 flex-wrap">
          <TokenMeter usage={tokenUsage} compact />
          <StatusBadge status={status} writesEnabled={status.writes_enabled} killEnabled={killEnabled} />
        </div>
      </div>

      {/* Sidebar */}
      <aside
        className={`${showMobileList ? "block" : "hidden"} lg:block w-full lg:w-72 shrink-0 rounded-2xl border border-[#CCE2F8] bg-white overflow-hidden flex flex-col`}
        aria-label="Lista de conversaciones"
      >
        <div className="p-3 border-b border-[#CCE2F8]">
          <Button
            onClick={newConv}
            data-testid="director-new-chat"
            className="w-full bg-[#0A2A5E] hover:bg-[#0A2A5E]/90"
          >
            <Plus size={14} className="mr-1.5" /> Nuevo chat
          </Button>
        </div>
        <div className="flex-1 overflow-y-auto">
          {convs.length === 0 ? (
            <div className="p-6 text-center text-xs text-slate-500">
              Aún no hay conversaciones. Crea la primera para empezar.
            </div>
          ) : convs.map((c) => (
            <button
              key={c.id}
              onClick={() => openConv(c.id)}
              data-testid={`conv-item-${c.id}`}
              className={`w-full text-left px-3 py-2.5 border-b border-slate-100 hover:bg-[#F3F8FE] transition-colors flex items-start gap-2 group ${
                activeId === c.id ? "bg-[#E6F2FF]" : ""
              }`}
            >
              <MessageSquare size={14} className="mt-0.5 text-[#0A2A5E] shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium truncate text-slate-800">{c.title}</div>
                <div className="text-[10px] text-slate-400">
                  {c.message_count} msgs · {fmtTime(c.updated_at)}
                </div>
              </div>
              <button
                type="button"
                onClick={(e) => deleteConv(c.id, e)}
                className="opacity-0 group-hover:opacity-100 text-slate-400 hover:text-red-600 transition-opacity"
                aria-label="Borrar conversación"
                data-testid={`conv-delete-${c.id}`}
              >
                <Trash2 size={13} />
              </button>
            </button>
          ))}
        </div>
      </aside>

      {/* Main pane */}
      <section
        className="flex-1 flex flex-col rounded-2xl border border-[#CCE2F8] bg-gradient-to-br from-white via-white to-[#E6F2FF]/40 overflow-hidden"
        aria-label="Director de Marketing — chat"
      >
        <header className="flex flex-wrap items-center justify-between gap-3 px-5 py-3 border-b border-[#CCE2F8] bg-white/80">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-xl bg-[#0A2A5E] text-white flex items-center justify-center">
              <Sparkles size={16} />
            </div>
            <div>
              <h2 className="text-base font-semibold text-[#0A2A5E]">Director de Marketing IA</h2>
              <p className="text-[11px] text-slate-500 hidden sm:block">Analiza. Propone. Ejecuta con tu aprobación.</p>
            </div>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <TokenMeter usage={tokenUsage} />
            <StatusBadge status={status} writesEnabled={status.writes_enabled} killEnabled={killEnabled} />
          </div>
        </header>

        {killEnabled && (
          <div role="alert" data-testid="director-kill-banner" className="bg-red-50 border-b border-red-200 px-4 py-2 text-xs text-red-700 flex items-center gap-2">
            <ShieldAlert size={14} /> El kill switch está activo. El chat y las aprobaciones están bloqueados.
          </div>
        )}

        <TokenAlertsBanner alerts={tokenAlerts} />

        <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 sm:p-5 space-y-4" data-testid="director-messages">
          {activeId === null ? (
            <div className="h-full flex items-center justify-center text-center p-6">
              <div className="max-w-md">
                <Sparkles className="mx-auto text-[#0A2A5E] mb-3" size={36} />
                <h3 className="text-xl font-semibold text-[#0A2A5E]">Hola, soy tu Director de Marketing IA</h3>
                <p className="mt-2 text-sm text-slate-600">
                  Analizo datos reales de GA4, Search Console, Brevo, WordPress, Meta y tu ERP para proponerte acciones basadas en evidencia.
                  Cualquier cambio en anuncios, posts o emails requerirá tu aprobación explícita.
                </p>
                <Button onClick={newConv} data-testid="director-cta-new" className="mt-5 bg-[#0A2A5E] hover:bg-[#0A2A5E]/90">
                  <Plus size={14} className="mr-1.5" /> Empezar un chat
                </Button>
              </div>
            </div>
          ) : messages.length === 0 ? (
            <div className="h-full flex items-center justify-center text-slate-400 text-sm">
              Haz tu primera pregunta. Ejemplo: "¿Qué tour tiene mejor margen y cómo puedo captarle más leads este mes?"
            </div>
          ) : (
            messages.map((m) => <Bubble key={m.id} msg={m} onProposalsChange={() => openConv(activeId)} />)
          )}
          {sending && (
            <div className="flex justify-start" data-testid="director-thinking">
              <div className="inline-flex items-center gap-2 rounded-2xl bg-white border border-[#CCE2F8] px-4 py-2.5 text-xs text-slate-500">
                <Loader2 size={13} className="animate-spin" /> Analizando datos reales…
              </div>
            </div>
          )}
        </div>

        <div className="border-t border-[#CCE2F8] p-3 bg-white/80">
          <div className="flex gap-2 items-end">
            <Textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); }
              }}
              placeholder={killEnabled ? "Chat bloqueado por kill switch." : "Pregunta algo o pide una acción (Shift+Enter para nueva línea)…"}
              rows={2}
              disabled={sending || killEnabled || status?.model_available === false}
              data-testid="director-input"
              className="flex-1 resize-none text-sm"
              aria-label="Mensaje para el Director IA"
            />
            <Button
              onClick={send}
              disabled={sending || killEnabled || !input.trim() || status?.model_available === false}
              data-testid="director-send"
              className="bg-[#0A2A5E] hover:bg-[#0A2A5E]/90 h-full px-4"
              aria-label="Enviar mensaje"
            >
              {sending ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
            </Button>
          </div>
          {status?.model_available === false && (
            <div className="mt-2 flex items-center gap-1.5 text-[11px] text-amber-700" data-testid="director-nomodel-note">
              <PlugZap size={11} /> Define `EMERGENT_LLM_KEY` desde Gestionar &gt; Secretos para activar al Director.
            </div>
          )}
          {!status?.writes_enabled && (
            <div className="mt-1 flex items-center gap-1.5 text-[10px] text-slate-400">
              <Lock size={10} /> Modo lectura: las propuestas quedan "preparadas, no ejecutadas" hasta que `MARKETING_WRITE_ENABLED=1`.
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
